import { useState, useEffect, useMemo } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  Text,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';

import LoadingOverlay from '../components/LoadingOverlay';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Util from '../util/util.js';
import { formatMediumDate } from '../util/format';
import { hroLabel } from '../util/constants';
import { t as translate, useTranslation } from '../i18n';
import { trackEngagementAsync } from '../util/engagementTracker';
import styles from '../style/screens/analyticsScreenStyles';
import { useResponsiveStyles, useCollapsibleSections } from '../hooks';
import {
  StatCard,
  PieChart,
  StatusCard,
  ProgressBarGroup,
  TimeFilterChips,
  KPIModal,
  AnalyticsChart,
  CollapsibleSection,
} from '../components/analytics';
import useAnalyticsData, { getTimeFilterOptions } from '../hooks/useAnalyticsData';
import AppStyles from '../AppStyles';

// Legend configurations - V3 unified color palette
const getAllReportsLegend = () => [
  { title: translate('analytics:reports'), color: AppStyles.colorSet.chartBlue },
];

// Stacked "Other" segment — muted so the folded tail doesn't compete with named series
const KPI_OTHER_COLOR = '#9ca3af';
const KPI_VISIBLE_SERIES = 5;

// Titles are built from the shared HRO labels so a category is only ever
// translated once; the code stays appended verbatim as an identifier.
const hroLegendItem = (code, color) => ({ title: `${hroLabel(code)} (${code})`, color });

const getAnticipationFailureLegend = () => [
  hroLegendItem('PF', AppStyles.colorSet.chartGray),
  hroLegendItem('RS', AppStyles.colorSet.chartAmber),
  hroLegendItem('SO', AppStyles.colorSet.chartBlue),
];

const getResponseDeviationsLegend = () => [
  hroLegendItem('CR', AppStyles.colorSet.chartPurple),
  hroLegendItem('DE', AppStyles.colorSet.chartRed),
];

const AnalyticsScreen = (props) => {
  const { t } = useTranslation('analytics');

  const { organization, admin, noLogin, location, userAdmin } = props.route.params;

  // Use responsive styles hook for platform detection
  const { isWeb, isDesktop, webStyles, mobileStyles } = useResponsiveStyles();

  // Collapsible sections state with persistence
  const { sectionStates, toggleSection, expandAll, collapseAll, allExpanded } = useCollapsibleSections();

  const [modalVisible, setModalVisible] = useState(false);

  // Recomputed each render so the chip copy follows a language change.
  const timeFilterOptions = getTimeFilterOptions();

  // Use analytics data hook
  const {
    allReports,
    reportsLoading,
    submittedReports,
    responseTime,
    openReports,
    closedReports,
    urgentReports,
    hroDistribution,
    kpiDistribution,
    allGraphStates,
    sortedKPIDatasetDetails,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    PreviousOrgFilterValue,
    setPreviousOrgFilterValue,
    OrgFilter,
    setOrgFilter,
    loadingOrg,
    activeFilter,
    setActiveFilter,
    analyticsCutoffDates,
    updateValidOrgs,
    updateValidKPIs,
    fetchReports,
  } = useAnalyticsData(organization, noLogin);

  // Track analytics view for surprise badges (fire once on mount)
  useEffect(() => {
    trackEngagementAsync('analytics_viewed');
  }, []);

  // Load data on mount and when org filter changes
  useEffect(() => {
    // Skip if dropdown is open (wait for user to finish selecting)
    if (OrgFilterOpen) return;

    // Compare arrays by value, not reference
    const orgValueChanged = JSON.stringify(OrgFilterValue) !== JSON.stringify(PreviousOrgFilterValue);
    if (!orgValueChanged) return;

    const loadData = async () => {
      if (noLogin) {
        await Util.executeQRSignIn(organization, noLogin);
      }
      await updateValidOrgs();
      await fetchReports();
      setPreviousOrgFilterValue([...OrgFilterValue]); // Store a copy
    };

    loadData();
  }, [OrgFilterValue, OrgFilterOpen]);

  // Update KPIs when reports load
  useEffect(() => {
    if (allReports.length > 0) {
      const routeKPIs = props.route.params?.validKPIs;
      if (routeKPIs?.length) {
        const kpis = routeKPIs.map((v) => v.value);
        updateValidKPIs(kpis);
      } else {
        updateValidKPIs([]);
      }
    }
  }, [allReports, OrgFilterValue]);

  // Stacked KPI chart: top 5 classifications by volume, remainder folded into "Other".
  // sortedKPIDatasetDetails is already sorted by sum (desc) and carries each
  // series' entity color, so ranks can change without repainting survivors.
  const kpiStack = useMemo(() => {
    const top = sortedKPIDatasetDetails.slice(0, KPI_VISIBLE_SERIES);
    const rest = sortedKPIDatasetDetails.slice(KPI_VISIBLE_SERIES);

    const datasets = top.map((detail) => detail.dataset);
    const legendItems = top.map((detail) => ({
      title: detail.label,
      color: detail.dataset.color(),
    }));

    if (rest.length > 0) {
      const otherData = new Array(allGraphStates.KPI.labels.length).fill(0);
      rest.forEach((detail) => {
        detail.dataset.data.forEach((value, i) => {
          otherData[i] += value;
        });
      });
      datasets.push({ data: otherData, color: () => KPI_OTHER_COLOR });
      legendItems.push({ title: t('other'), color: KPI_OTHER_COLOR });
    }

    return {
      data: { labels: allGraphStates.KPI.labels, datasets },
      legendItems,
    };
  }, [sortedKPIDatasetDetails, allGraphStates.KPI.labels]);

  // Organization filter dropdown
  const renderOrgFilter = (isWeb = false) => {
    if (OrgFilter == null || OrgFilter.length <= 1) return null;

    return (
      <View style={isWeb ? styles.orgFilterWeb : styles.orgFilterMobile}>
        <DropDownPicker
          loading={loadingOrg}
          zIndex={9}
          multiple={true}
          min={0}
          open={OrgFilterOpen}
          value={OrgFilterValue}
          items={OrgFilter}
          setOpen={setOrgFilterOpen}
          setValue={setOrgFilterValue}
          setItems={setOrgFilter}
          dropDownDirection="BOTTOM"
          placeholder={t('filterByOrganization')}
          maxHeight={158}
          style={styles.dropdownStyle}
          dropDownContainerStyle={styles.dropdownContainerStyle}
          textStyle={styles.dropdownTextStyle}
          placeholderStyle={styles.dropdownPlaceholderStyle}
          disabledStyle={{ opacity: 1 }}
        />
      </View>
    );
  };

  // Determine response time color based on value (red if > 48h, amber if > 24h, green otherwise)
  const getResponseTimeColor = () => {
    if (responseTime === '---') return '#22c55e';
    const hours = parseFloat(responseTime);
    if (hours > 48) return '#ef4444'; // Red for > 48h
    if (hours > 24) return '#f59e0b'; // Amber for > 24h
    return '#22c55e'; // Green for acceptable
  };

  // Get response time subtitle based on status
  const getResponseTimeSubtitle = () => {
    if (responseTime === '---') return null;
    const hours = parseFloat(responseTime);
    if (hours > 48) return t('needsAttention');
    if (hours > 24) return t('aboveTarget');
    return t('onTrack');
  };

  // Stats cards section
  const renderStatsCards = (isWeb = false) => (
    <>
      <View style={[styles.statsGrid, isWeb && styles.statsGridWeb]}>
        <StatCard
          title={t('totalReports')}
          value={submittedReports}
          icon="file-text"
          color="#3b82f6"
          subtitle={closedReports > 0 ? t('resolved', { count: closedReports }) : null}
          style={styles.statCardHalf}
        />
        <StatCard
          title={t('avgResponseTime')}
          value={responseTime === '---' ? '—' : `${Math.round(parseFloat(responseTime))}h`}
          icon="clock"
          color={getResponseTimeColor()}
          subtitle={getResponseTimeSubtitle()}
          style={styles.statCardHalf}
        />
        {isWeb && (
          <>
            <StatCard
              title={t('urgentReports')}
              value={urgentReports}
              icon="alert-triangle"
              color="#ef4444"
              subtitle={urgentReports > 0 ? t('requiresAction') : t('allClear')}
              style={styles.statCardHalf}
            />
            <StatCard
              title={t('activeReports')}
              value={openReports}
              icon="inbox"
              color="#f59e0b"
              subtitle={openReports > 0 ? t('awaitingReview') : t('nonePending')}
              style={styles.statCardHalf}
            />
          </>
        )}
      </View>
      {!isWeb && (
        <View style={styles.statsGrid}>
          <StatCard
            title={t('urgentReports')}
            value={urgentReports}
            icon="alert-triangle"
            color="#ef4444"
            subtitle={urgentReports > 0 ? t('requiresAction') : t('allClear')}
            style={styles.statCardHalf}
          />
          <StatCard
            title={t('activeReports')}
            value={openReports}
            icon="inbox"
            color="#f59e0b"
            subtitle={openReports > 0 ? t('awaitingReview') : t('nonePending')}
            style={styles.statCardHalf}
          />
        </View>
      )}
      {(() => {
        // Show cutoff indicator if any viewed org has a cutoff date set
        const cutoffOrgs = Object.entries(analyticsCutoffDates || {}).filter(([, date]) => date);
        if (cutoffOrgs.length === 0) return null;

        // Use consistent format for single or multiple orgs
        const orgList = cutoffOrgs.map(([org, date]) =>
          `${org} (${formatMediumDate(date)})`
        ).join(', ');

        return (
          <View style={styles.cutoffIndicator}>
            <Feather name="info" size={14} color="#4B5563" />
            <Text style={styles.cutoffIndicatorText}>
              {t('cutoffIndicator', { organizations: orgList })}
            </Text>
          </View>
        );
      })()}
    </>
  );

  // Distribution charts section
  const renderDistributionCharts = (isWeb = false) => (
    <View style={[styles.statsGrid, isWeb && styles.statsGridWeb]}>
      <StatusCard
        open={openReports}
        closed={closedReports}
        title={t('reportStatus')}
        style={{ flex: 1 }}
      />
      {hroDistribution.length > 0 && (
        <PieChart
          data={hroDistribution}
          size={isWeb ? 140 : 160}
          innerRadius={0.55}
          title={t('hroClassification')}
          style={{ flex: 1 }}
        />
      )}
      {kpiDistribution.length > 0 && (
        <ProgressBarGroup
          title={t('topHazards')}
          items={kpiDistribution}
          style={{ flex: 1 }}
        />
      )}
    </View>
  );

  // Web layout
  if (isDesktop) {
    return (
      <View style={webStyles.container}>
        <Header
          organization={organization}
          admin={admin}
          web={true}
          noLogin={noLogin}
          type={noLogin ? 'pop' : 'standard'}
          location={location}
          userAdmin={userAdmin}
        />
        <ScrollView style={[{ maxHeight: '100vh' }, styles.container]}>
          <View style={styles.filterRow}>
            {renderOrgFilter(true)}
          </View>

          <View style={[styles.chipContainer, { zIndex: -1 }]}>
            <TimeFilterChips
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              options={timeFilterOptions}
            />
            {reportsLoading && <LoadingOverlay message={t('loading')} />}
          </View>

          {renderStatsCards(true)}

          <View style={styles.expandCollapseRow}>
            <Pressable
              onPress={allExpanded ? collapseAll : expandAll}
              style={({ pressed, hovered, focused }) => [
                styles.expandCollapseButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.expandCollapseHovered,
                pressed && styles.expandCollapsePressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={allExpanded ? t('collapseAllLabel') : t('expandAllLabel')}
            >
              <Feather
                name={allExpanded ? 'minimize-2' : 'maximize-2'}
                size={16}
                color="#6B7280"
              />
              <Text style={styles.expandCollapseText}>
                {allExpanded ? t('collapseAll') : t('expandAll')}
              </Text>
            </Pressable>
          </View>

          <CollapsibleSection
            title={t('statusOverview')}
            subtitle={t('activeClosedSummary', { active: openReports, closed: closedReports })}
            isExpanded={sectionStates.statusOverview}
            onToggle={() => toggleSection('statusOverview')}
          >
            {renderDistributionCharts(true)}
          </CollapsibleSection>

          <CollapsibleSection
            title={t('hroTrends')}
            subtitle={t('hroTrendsSubtitle')}
            isExpanded={sectionStates.hroTrends}
            onToggle={() => toggleSection('hroTrends')}
          >
            <AnalyticsChart
              type="stackedBar"
              title={t('anticipationOfFailure')}
              data={allGraphStates.AF}
              legendItems={getAnticipationFailureLegend()}
              isWeb={true}
            />
            <AnalyticsChart
              type="stackedBar"
              title={t('responseToDeviations')}
              data={allGraphStates.RD}
              legendItems={getResponseDeviationsLegend()}
              isWeb={true}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={t('classifications')}
            subtitle={kpiDistribution.length > 0 ? `Top: ${kpiDistribution[0]?.label}` : t('classificationsSubtitle')}
            isExpanded={sectionStates.classifications}
            onToggle={() => toggleSection('classifications')}
          >
            <AnalyticsChart
              type="stackedBar"
              title={t('hazardClassifications')}
              data={kpiStack.data}
              legendItems={kpiStack.legendItems}
              onPress={() => setModalVisible(true)}
              isWeb={true}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={t('allReports')}
            subtitle={`${submittedReports} total reports`}
            isExpanded={sectionStates.allReports}
            onToggle={() => toggleSection('allReports')}
          >
            <AnalyticsChart
              type="bar"
              title={t('allReports')}
              data={allGraphStates.AR}
              legendItems={getAllReportsLegend()}
              isWeb={true}
            />
          </CollapsibleSection>
        </ScrollView>

        <KPIModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          labels={allGraphStates.KPI.labels}
          datasetDetails={sortedKPIDatasetDetails}
        />

        <Footer
          admin={admin}
          organization={organization}
          noLogin={noLogin}
          location={location}
          userAdmin={userAdmin}
        />
      </View>
    );
  }

  // Mobile layout
  return (
    <View style={[mobileStyles.container, styles.container]}>
      <Header
        organization={organization}
        admin={admin}
        noLogin={noLogin}
        location={location}
        userAdmin={userAdmin}
      />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={styles.mobileFilterContainer}>
          {renderOrgFilter(false)}
        </View>

        <View style={[styles.chipContainer, { zIndex: -1 }]}>
          <TimeFilterChips
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            options={timeFilterOptions}
          />
        </View>

        {reportsLoading && <LoadingOverlay message={t('loading')} />}

        {renderStatsCards(false)}

        <View style={styles.expandCollapseRow}>
          <Pressable
            onPress={allExpanded ? collapseAll : expandAll}
            style={({ pressed }) => [styles.expandCollapseButton, pressed && styles.expandCollapsePressed]}
            accessibilityRole="button"
            accessibilityLabel={allExpanded ? t('collapseAllLabel') : t('expandAllLabel')}
          >
            <Feather
              name={allExpanded ? 'minimize-2' : 'maximize-2'}
              size={16}
              color="#6B7280"
            />
            <Text style={styles.expandCollapseText}>
              {allExpanded ? t('collapseAll') : t('expandAll')}
            </Text>
          </Pressable>
        </View>

        <CollapsibleSection
          title={t('statusOverview')}
          subtitle={t('activeClosedSummary', { active: openReports, closed: closedReports })}
          isExpanded={sectionStates.statusOverview}
          onToggle={() => toggleSection('statusOverview')}
        >
          <View style={styles.chartSection}>
            <StatusCard open={openReports} closed={closedReports} title={t('reportStatus')} />
          </View>

          {hroDistribution.length > 0 && (
            <View style={styles.chartSection}>
              <PieChart
                data={hroDistribution}
                size={160}
                innerRadius={0.55}
                title={t('hroClassification')}
              />
            </View>
          )}

          {kpiDistribution.length > 0 && (
            <View style={styles.chartSection}>
              <ProgressBarGroup title={t('topHazards')} items={kpiDistribution} />
            </View>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title={t('hroTrends')}
          subtitle={t('hroTrendsSubtitle')}
          isExpanded={sectionStates.hroTrends}
          onToggle={() => toggleSection('hroTrends')}
        >
          <AnalyticsChart
            type="stackedBar"
            title={t('anticipationOfFailure')}
            data={allGraphStates.AF}
            legendItems={getAnticipationFailureLegend()}
            isWeb={false}
          />
          <AnalyticsChart
            type="stackedBar"
            title={t('responseToDeviations')}
            data={allGraphStates.RD}
            legendItems={getResponseDeviationsLegend()}
            isWeb={false}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title={t('classifications')}
          subtitle={kpiDistribution.length > 0 ? `Top: ${kpiDistribution[0]?.label}` : t('classificationsSubtitle')}
          isExpanded={sectionStates.classifications}
          onToggle={() => toggleSection('classifications')}
        >
          <AnalyticsChart
            type="stackedBar"
            title={t('hazardClassifications')}
            data={kpiStack.data}
            legendItems={kpiStack.legendItems}
            onPress={() => setModalVisible(true)}
            isWeb={false}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title={t('allReports')}
          subtitle={`${submittedReports} total reports`}
          isExpanded={sectionStates.allReports}
          onToggle={() => toggleSection('allReports')}
        >
          <AnalyticsChart
            type="bar"
            title={t('allReports')}
            data={allGraphStates.AR}
            legendItems={getAllReportsLegend()}
            isWeb={false}
          />
        </CollapsibleSection>
      </ScrollView>

      <KPIModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        labels={allGraphStates.KPI.labels}
        datasetDetails={sortedKPIDatasetDetails}
      />

      <Footer
        admin={admin}
        organization={organization}
        noLogin={noLogin}
        location={location}
        userAdmin={userAdmin}
      />
    </View>
  );
};

export default AnalyticsScreen;
